import { effect, Injectable } from '@angular/core';
import { LeadConversationStateStore } from '../dialpad/leadConversation.state';
import { AdmissionUIMessengerService } from './admission-ui-messenger.service';
import { AdmissionUICommonDataService } from './admission-ui-common-data.service';
import { Models } from 'purecloud-platform-client-v2';

/**
 * State data for interaction
 */
export interface StateData {
  interactionType: string | undefined;
  studentId: number | undefined;
  phoneNumber: string | undefined;
  emailConversationId: string | undefined;
  dialerContactId: string | undefined;
  telecomDialerCampaignName: string | undefined;
  callConversationId: string | undefined;
  callDuration: number | undefined;
  timestamp: number;
  lastActivePageName: string | undefined;
  callStartTimestamp: number | undefined;
  dispositionStartTimestamp: number | undefined;
  previousUserStatus: string | undefined;
  pstSMSDisabled: boolean | undefined;
  pstEmailDisabled: boolean | undefined;
  stitchInWindow: boolean | undefined;
  callStatus: string;
}

/**
 * Service for saving and retrieving state data for interactions
 * Handles calls and email interactions
 */
@Injectable({
  providedIn: 'root'
})
export class InteractionStateService {
  private readonly STORAGE_KEY = 'interaction_state';
  private cancelState = false; // Flag to cancel state saving
  constructor(
    private readonly leadConversationStateStore: LeadConversationStateStore,
    private readonly messenger: AdmissionUIMessengerService,
    private readonly commonDataService: AdmissionUICommonDataService,

  ) {
    // Set up an effect to track changes in the lead conversation state
    effect(() => {
      // Don't update if cancel state is true
      if (this.cancelState) {
        return;
      }

      const leadState = this.leadConversationStateStore.getState()();
      
      let associatedEmail :string | undefined = '';
      if(leadState?.Call){
        associatedEmail = leadState?.Call?.participants?.[0].attributes?.['leadFlow_Associated_EmailConversationId'];
        if(associatedEmail && leadState?.Email?.id !== associatedEmail){
          console.log('Clearing Call Conversation as associated email does not match. Associated Email:', associatedEmail, 'Email Conversation ID in state:', leadState?.Email?.id);
          this.leadConversationStateStore.updateCallConversation({} as Models.CallConversation);
          return;
        }
      }
      // Only save state if there is an active conversation
      if (leadState && leadState.LastActivePageName !== 'Idle') {
        const stateData: StateData = this.getDefaultState();

        stateData.callConversationId = leadState?.Call?.id;
        stateData.interactionType = leadState.CallType;
        stateData.phoneNumber = leadState.PhoneNumber;
        stateData.emailConversationId = leadState?.Email?.id;
        stateData.dialerContactId = leadState?.DialerContactId;
        stateData.telecomDialerCampaignName = leadState?.TelecomDialerCampaignName;
        stateData.lastActivePageName = leadState?.LastActivePageName;
        stateData.studentId = leadState?.Student?.id;
        stateData.callStartTimestamp = leadState?.CallStartTimestamp;
        stateData.dispositionStartTimestamp = leadState?.DispositionStartTimestamp;
        stateData.previousUserStatus = leadState?.PreviousUserStatus;
        stateData.pstSMSDisabled = leadState?.PSTSMSDisabled;
        stateData.pstEmailDisabled = leadState?.PSTEmailDisabled;
        stateData.callStatus = leadState?.CallStatus ?? "Not Set";
        stateData.stitchInWindow = leadState?.StitchInWindow;
        // Save the state
        this.saveState(stateData);
      }
    });

    // Subscribe to clear interaction state events
    this.messenger.clearInteractionState$.subscribe(clear => {
      if (clear) {
        this.clearState();
      }
    });
  }

  /**
   * Enables or disables the cancel state flag
   * 
   * @param cancel Whether to cancel state saving
   */
  setCancelState(cancel: boolean): void {
    this.clearState(); // Clear the state when canceling
    this.cancelState = cancel;
  }

  /**
   * Creates a default state object
   * @private
   */
  private getDefaultState(): StateData {
    return {
      studentId: undefined,
      interactionType: undefined,
      phoneNumber: undefined,
      dialerContactId: undefined,
      telecomDialerCampaignName: undefined,
      emailConversationId: undefined,
      callConversationId: undefined,
      callDuration: undefined,
      lastActivePageName: undefined,
      timestamp: 0,
      callStartTimestamp: undefined,
      dispositionStartTimestamp: undefined,
      previousUserStatus: undefined,
      pstSMSDisabled: undefined,
      pstEmailDisabled: undefined,
      stitchInWindow: undefined,
      callStatus: 'Not Set'
    };
  }

  /**
   * Saves complete state data
   * 
   * @param data The data to save
   * @returns void
   */
  saveState(data: StateData): void {
    if (this.cancelState) {
      return;
    }
    if (!this.isValidState(data)) {
      return;
    }

    // Add timestamp to data
    const stateData: StateData = {
      ...data,
      timestamp: Date.now()
    };

    // Serialize and store in localStorage
    const serializedData = JSON.stringify(stateData);
    localStorage.setItem(this.STORAGE_KEY, serializedData);
  }

  /**
   * Retrieves the current interaction state data
   * 
   * @returns The saved state data, or null if none exists
   */
  getState(): StateData | null {
    const storedData = localStorage.getItem(this.STORAGE_KEY);

    if (!storedData) {
      return null;
    }

    try {
      return JSON.parse(storedData) as StateData;
    } catch (e) {
      return null;
    }
  }

  /**
   * Clears the saved interaction state
   */
  clearState(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.cancelState = false; // Reset the cancel state flag
  }

  /**
   * Validates if the provided object is a valid StateData object
   * To be valid, it should contain at least one property other than timestamp,
   * and if lastActivePageName is the only property, it shouldn't be 'Idle'
   * 
   * @param state The object to validate
   * @returns boolean indicating if the object is a valid StateData
   */
  isValidState(state: StateData | any): boolean {
    if (!state) {
      return false;
    }

    // Check if any meaningful property exists (other than timestamp)
    const hasInteractionType = !!state.interactionType;
    const hasStudentId = !!state.studentId;
    const hasPhoneNumber = !!state.phoneNumber;
    const hasDialerContactId = !!state.dialerContactId;
    const hasEmailConversationId = !!state.emailConversationId;
    const hasCallConversationId = !!state.callConversationId;
    const hasCallDuration = !!state.callDuration;
    const isAlerting = this.commonDataService.alertingStatus.includes(state.callStatus);
    const isNotMultiMatch = state.lastActivePageName !== 'MultipleMatch'
    // Check if lastActivePageName is valid (not 'Idle')
    const hasValidPageName = !!state.lastActivePageName && state.lastActivePageName !== 'Idle';

    if (isAlerting && isNotMultiMatch)
      return false;

    // Check if at least one meaningful property exists
    const hasAnyMeaningfulProperty =
      hasInteractionType ||
      hasStudentId ||
      hasPhoneNumber ||
      hasDialerContactId ||
      hasEmailConversationId ||
      hasCallConversationId ||
      hasCallDuration ||
      hasValidPageName;

    return hasAnyMeaningfulProperty;
  }
}